import { useState, useEffect, memo } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dumbbell,
  Scale,
  Leaf,
  Carrot,
  Clock,
  UtensilsCrossed,
  Users,
  PiggyBank,
  Tag
} from 'lucide-react';

// Icon mapping for health labels
const LABEL_ICONS = {
  'high-protein': Dumbbell,
  'balanced': Scale,
  'lower-carb': Leaf,
  'veggie-forward': Carrot,
  'quick-prep': Clock,
  'one-pan': UtensilsCrossed,
  'family-approved': Users,
  'budget-smart': PiggyBank,
  'default': Tag
};

// Color variants for labels (subtle, brand-safe)
const LABEL_COLORS = {
  'high-protein': 'bg-blue-50 text-blue-700 border-blue-200',
  'balanced': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'lower-carb': 'bg-amber-50 text-amber-700 border-amber-200',
  'veggie-forward': 'bg-green-50 text-green-700 border-green-200',
  'quick-prep': 'bg-purple-50 text-purple-700 border-purple-200',
  'one-pan': 'bg-orange-50 text-orange-700 border-orange-200',
  'family-approved': 'bg-pink-50 text-pink-700 border-pink-200',
  'budget-smart': 'bg-teal-50 text-teal-700 border-teal-200',
  'default': 'bg-gray-50 text-gray-700 border-gray-200'
};

/**
 * Display health labels as badges
 * Supports both our internal labels and Edamam health labels
 */
const HealthLabelBadges = memo(({ 
  labels = [], 
  healthLabels = [],  // From Edamam
  dietLabels = [],    // From Edamam
  maxDisplay = 3,
  size = 'sm',        // 'xs', 'sm', 'md'
  showTooltip = true,
  className = ''
}) => {
  // Combine and normalize all labels
  const allLabels = [
    ...labels.map(l => typeof l === 'string' ? { id: l, name: l } : l),
    ...healthLabels.map(l => ({ id: l.toLowerCase().replace(/\s+/g, '-'), name: l, source: 'edamam' })),
    ...dietLabels.map(l => ({ id: l.toLowerCase().replace(/\s+/g, '-'), name: l, source: 'edamam' }))
  ];

  // Deduplicate
  const uniqueLabels = allLabels.reduce((acc, label) => {
    if (!acc.find(l => l.id === label.id)) {
      acc.push(label);
    }
    return acc;
  }, []);

  if (uniqueLabels.length === 0) return null;

  const displayLabels = uniqueLabels.slice(0, maxDisplay);
  const remainingCount = uniqueLabels.length - maxDisplay;

  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1'
  };

  const iconSizes = {
    xs: 'w-2.5 h-2.5',
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5'
  };

  const renderLabel = (label) => {
    const IconComponent = LABEL_ICONS[label.id] || LABEL_ICONS.default;
    const colorClass = LABEL_COLORS[label.id] || LABEL_COLORS.default;
    
    const badge = (
      <Badge
        key={label.id}
        variant="outline"
        className={`
          ${colorClass} 
          ${sizeClasses[size]} 
          font-medium border
          flex items-center gap-1
        `}
        data-testid={`health-label-${label.id}`}
      >
        <IconComponent className={iconSizes[size]} />
        <span className="capitalize">{label.name}</span>
      </Badge>
    );

    if (showTooltip && label.description) {
      return (
        <Tooltip key={label.id}>
          <TooltipTrigger asChild>
            {badge}
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs">{label.description}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return badge;
  };

  return (
    <TooltipProvider>
      <div className={`flex flex-wrap gap-1 ${className}`} data-testid="health-labels">
        {displayLabels.map(renderLabel)}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className={`${sizeClasses[size]} bg-gray-50 text-gray-600 border-gray-200`}
              >
                +{remainingCount}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">
                {uniqueLabels.slice(maxDisplay).map(l => l.name).join(', ')}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
});

HealthLabelBadges.displayName = 'HealthLabelBadges';

export default HealthLabelBadges;
